import { useEffect, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const Header = (props) => {
  const { t } = useTranslation("clientSite");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("home");

  const navItems = [
    { id: "home", label: t("header.nav.home") },
    props.site?.aboutUsPageEnabled ? { id: "about", label: t("header.nav.about") } : null,
    props.site?.servicePageEnabled ? { id: "services", label: t("header.nav.services") } : null,
    props.site?.staffPageEnabled ? { id: "staff", label: t("header.nav.team") } : null,
  ].filter(Boolean);
  const navIds = navItems.map((item) => item.id).join(",");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Highlight whichever section is currently in view, so the nav
  // always shows the visitor where they are on the page.
  useEffect(() => {
    const ids = navIds ? navIds.split(",") : [];
    const targets = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [navIds]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const header = document.querySelector(".site-header");
    const offset = (header?.offsetHeight || 88) + 12;
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
    setMenuOpen(false);
  };

  const logoSrc = props.site?.logoFilename
    ? process.env.REACT_APP_API_SERVICE_BASE_URL + "image/SiteLogoImages/" + props.site.logoFilename
    : null;

  return (
    <header className={`site-header ${scrolled ? "site-header--scrolled" : ""}`}>
      <div className="site-container site-header__inner">
        <button
          type="button"
          className="site-brand"
          onClick={() => scrollToSection("home")}
          aria-label={props.site?.siteTitle || t("header.nav.home")}
        >
          {logoSrc ? (
            <img className="site-brand__logo" src={logoSrc} alt={props.site?.siteTitle || "logo"} />
          ) : (
            <span className="site-brand__logo site-brand__logo--fallback" aria-hidden="true">
              {props.site?.siteTitle ? props.site.siteTitle.charAt(0).toUpperCase() : "?"}
            </span>
          )}
          <span className="site-brand__text">{props.site?.siteTitle}</span>
        </button>

        <nav className="site-nav site-nav--desktop" aria-label="Primary navigation">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`site-nav__link ${activeId === item.id ? "is-active" : ""}`}
              aria-current={activeId === item.id ? "true" : undefined}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
          <a className="site-bookBtn" href={`make-a-booking?id=${props.site.barberShopId}`} target="_blank" rel="noreferrer">
            {t("bookNow")}
          </a>
        </nav>

        <button
          type="button"
          className="site-menuToggle"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? t("header.aria.closeMenu") : t("header.aria.openMenu")}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <div className={`site-mobileMenu ${menuOpen ? "is-open" : ""}`}>
        <div className="site-mobileMenu__panel">
          <div className="site-mobileMenu__top">
            <div>
              <p className="site-mobileMenu__eyebrow">{t("header.menu")}</p>
              <h2>{props.site?.siteTitle}</h2>
            </div>
            <button type="button" className="site-mobileMenu__close" onClick={() => setMenuOpen(false)} aria-label={t("header.aria.closeMenu")}>
              <FaTimes />
            </button>
          </div>

          <div className="site-mobileMenu__links">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`site-mobileMenu__link ${activeId === item.id ? "is-active" : ""}`}
                onClick={() => scrollToSection(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <a className="site-bookBtn site-bookBtn--mobile" href={`make-a-booking?id=${props.site.barberShopId}`} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>
            {t("bookNow")}
          </a>

          <p className="site-mobileMenu__foot">
            {t("header.messages.poweredBy", { brand: process.env.REACT_APP_COMPANY_NAME })}
          </p>
        </div>
      </div>
    </header>
  );
};

export default Header;
